if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

      /**
       * ==========================================================
       * CUSTOM CHANGE (Faunus Plant) - Variant-based gallery filter
       * ==========================================================
       * Filter gallery images by parsing `alt` text rules:
       * - If an image alt contains: `... | OptionName - OptionValue`
       *   it will ONLY show when the currently selected options match.
       * - Images without any valid `| OptionName - OptionValue` tokens
       *   remain visible for ALL options.
       *
       * Notes:
       * - Works in addition to the theme's "media grouping" feature.
       * - Supports multiple tokens in one alt (AND logic for known option names).
       */
      // CUSTOM CHANGE (Faunus Plant): capture base visibility from theme, then apply filtering.
      this.captureThemeBaseVisibility();
      this.applyAltVariantFilterFromCurrentState();
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      // CUSTOM CHANGE (Faunus Plant): always reset our previous filter before theme/grouping logic runs.
      this.resetAltFilterToThemeBase();

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      // CUSTOM CHANGE (Faunus Plant): capture base visibility from theme/grouping, then apply filtering.
      this.captureThemeBaseVisibility();
      this.applyAltVariantFilterFromEvent(evt);

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    // ============================
    // CUSTOM CHANGE (Faunus Plant)
    // ============================
    normalizeOptionName(str) {
      return String(str || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    normalizeOptionValue(str) {
      // Make matching tolerant (e.g. "200 ml" == "200ml", case-insensitive).
      const v = String(str || '').trim().toLowerCase().replace(/\s+/g, ' ');
      // Join common unit formats: "200 ml" -> "200ml"
      return v.replace(/(\d)\s+(ml|g|kg|l)\b/g, '$1$2');
    }

    parseAltConstraints(altText, optionNamesSet) {
      const alt = String(altText || '').trim();
      if (!alt.includes('|')) return [];

      // everything after first '|' can contain one or multiple rules, separated by '|'
      const parts = alt.split('|').slice(1).map((p) => p.trim()).filter(Boolean);
      const constraints = [];

      parts.forEach((part) => {
        // Expected format: "OptionName - OptionValue"
        const match = part.match(/^(.+?)\s*-\s*(.+)$/);
        if (!match) return;
        const name = this.normalizeOptionName(match[1]);
        const value = this.normalizeOptionValue(match[2]);
        if (!name || !value) return;
        if (!optionNamesSet.has(name)) return; // ignore unknown option names
        constraints.push({ name, value });
      });

      return constraints;
    }

    getSelectedOptionMapFromEvent(evt) {
      const product = evt?.detail?.product;
      const variant = evt?.detail?.variant;
      if (!product || !variant) return null;
      if (!Array.isArray(product.options) || !Array.isArray(variant.options)) return null;

      const map = new Map();
      product.options.forEach((name, i) => {
        map.set(this.normalizeOptionName(name), this.normalizeOptionValue(variant.options[i]));
      });
      return map;
    }

    getSelectedOptionMapFromDOM() {
      // Used for initial page load (before the first on:variant:change event).
      const variantPicker = this.section?.querySelector('variant-picker');
      const dataEl = variantPicker?.querySelector('[type="application/json"]');
      if (!dataEl) return null;

      let product;
      try {
        product = JSON.parse(dataEl.textContent)?.product;
      } catch (e) {
        return null;
      }
      if (!product || !Array.isArray(product.options)) return null;

      const selectedValues = [];
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedValues.push(selector.querySelector('.custom-select__btn')?.textContent?.trim() || null);
        } else {
          selectedValues.push(selector.querySelector('input:checked')?.value || null);
        }
      });

      const map = new Map();
      product.options.forEach((name, i) => {
        map.set(this.normalizeOptionName(name), this.normalizeOptionValue(selectedValues[i]));
      });
      return map;
    }

    getMediaAltText(mediaItem) {
      // Prefer the main product image alt; fallback to zoom image alt.
      const img = mediaItem.querySelector('img.product-image');
      if (img?.getAttribute('alt')) return img.getAttribute('alt');
      const zoomImg = mediaItem.querySelector('img.zoom-image');
      if (zoomImg?.getAttribute('alt')) return zoomImg.getAttribute('alt');
      return '';
    }

    getCachedConstraintsForItem(mediaItem, optionNamesSet) {
      // Parse alt once per media id for performance.
      if (!this.ccAltConstraintsByMediaId) this.ccAltConstraintsByMediaId = new Map();
      const mediaId = mediaItem?.dataset?.mediaId;
      if (!mediaId) return [];

      // CUSTOM CHANGE (Faunus Plant): Prefer pre-parsed Liquid constraints for speed.
      // data-cc-alt-filters="cantitate=200ml;aroma=menta"
      const dsFilters = mediaItem?.dataset?.ccAltFilters;
      if (dsFilters) {
        const parsed = String(dsFilters)
          .split(';')
          .map((p) => p.trim())
          .filter(Boolean)
          .map((p) => {
            const idx = p.indexOf('=');
            if (idx === -1) return null;
            const name = this.normalizeOptionName(p.slice(0, idx));
            const value = this.normalizeOptionValue(p.slice(idx + 1));
            if (!name || !value) return null;
            return { name, value };
          })
          .filter(Boolean);
        return parsed.filter((c) => optionNamesSet.has(c.name));
      }

      if (!this.ccAltConstraintsByMediaId.has(mediaId)) {
        const alt = this.getMediaAltText(mediaItem);
        // Store constraints WITHOUT filtering by optionNamesSet first; we filter at match time too.
        // However, we still normalize values here.
        const rawAlt = String(alt || '').trim();
        const allParts = rawAlt.includes('|')
          ? rawAlt.split('|').slice(1).map((p) => p.trim()).filter(Boolean)
          : [];
        const parsed = [];
        allParts.forEach((part) => {
          const match = part.match(/^(.+?)\s*-\s*(.+)$/);
          if (!match) return;
          const name = this.normalizeOptionName(match[1]);
          const value = this.normalizeOptionValue(match[2]);
          if (!name || !value) return;
          parsed.push({ name, value });
        });
        this.ccAltConstraintsByMediaId.set(mediaId, parsed);
      }

      // Filter to only option names that exist on the product (optionNamesSet).
      const constraints = this.ccAltConstraintsByMediaId.get(mediaId) || [];
      return constraints.filter((c) => optionNamesSet.has(c.name));
    }

    captureThemeBaseVisibility() {
      // Capture the current display state set by the theme (and optional media grouping).
      // IMPORTANT: This must be called BEFORE applying our alt-based filter and AFTER
      // any theme/grouping visibility updates have run.
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');

      this.viewerItems.forEach((el) => {
        el.dataset.ccThemeBaseDisplay = el.style.display || '';
        // CUSTOM CHANGE (Faunus Plant):
        // If Liquid pre-hid this item due to option filters, treat "theme base" as NOT hidden,
        // otherwise it will remain hidden forever after variant changes.
        el.dataset.ccThemeBaseHidden = el.dataset.ccAltFilters ? '0' : (el.hidden ? '1' : '0');
      });
      this.thumbsItems.forEach((el) => {
        el.dataset.ccThemeBaseDisplay = el.style.display || '';
        el.dataset.ccThemeBaseHidden = el.dataset.ccAltFilters ? '0' : (el.hidden ? '1' : '0');
      });
    }

    resetAltFilterToThemeBase() {
      // Restore inline display to what the theme last set (so our previous filter doesn't "stick").
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');

      this.viewerItems.forEach((el) => {
        el.style.display = el.dataset.ccThemeBaseDisplay || '';
        el.hidden = el.dataset.ccThemeBaseHidden === '1';
      });
      this.thumbsItems.forEach((el) => {
        el.style.display = el.dataset.ccThemeBaseDisplay || '';
        el.hidden = el.dataset.ccThemeBaseHidden === '1';
      });
    }

    applyAltVariantFilter(selectedOptionMap) {
      if (!selectedOptionMap) return;

      const optionNamesSet = new Set(Array.from(selectedOptionMap.keys()));

      // Filter main viewer items.
      this.viewerItems.forEach((item) => {
        // Only images are filtered by alt; other media types stay visible.
        if (item.dataset.mediaType && item.dataset.mediaType !== 'image') return;

        const constraints = this.getCachedConstraintsForItem(item, optionNamesSet);

        // No valid constraints => show for all options.
        if (constraints.length === 0) {
          item.style.display = item.dataset.ccThemeBaseDisplay || '';
          item.hidden = item.dataset.ccThemeBaseHidden === '1';
          return;
        }

        // AND logic: all known constraints in alt must match the selected options.
        const matchesAll = constraints.every((c) => selectedOptionMap.get(c.name) === c.value);
        item.style.display = item.dataset.ccThemeBaseDisplay || '';
        item.hidden = matchesAll ? (item.dataset.ccThemeBaseHidden === '1') : true;
      });

      // Filter thumbnail items (match by data-media-id to the viewer item alt).
      this.thumbsItems.forEach((thumb) => {
        const mediaId = thumb.dataset.mediaId;
        const viewerItem = mediaId ? this.querySelector(`.media-viewer__item[data-media-id="${mediaId}"]`) : null;
        if (!viewerItem) return;

        if (viewerItem.dataset.mediaType && viewerItem.dataset.mediaType !== 'image') return;

        const constraints = this.getCachedConstraintsForItem(viewerItem, optionNamesSet);

        if (constraints.length === 0) {
          thumb.style.display = thumb.dataset.ccThemeBaseDisplay || '';
          thumb.hidden = thumb.dataset.ccThemeBaseHidden === '1';
          return;
        }

        const matchesAll = constraints.every((c) => selectedOptionMap.get(c.name) === c.value);
        thumb.style.display = thumb.dataset.ccThemeBaseDisplay || '';
        thumb.hidden = matchesAll ? (thumb.dataset.ccThemeBaseHidden === '1') : true;
      });

      // Recompute visible items + counters and ensure active item is visible.
      this.setVisibleItems();
      if (this.visibleItems.length === 0) return;

      const currentIsVisible = this.currentItem && this.currentItem.clientWidth > 0;
      if (!currentIsVisible) {
        this.customSetActiveMedia(this.visibleItems[0], true);
      } else if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    applyAltVariantFilterFromEvent(evt) {
      const map = this.getSelectedOptionMapFromEvent(evt);
      if (map) this.applyAltVariantFilter(map);
    }

    applyAltVariantFilterFromCurrentState() {
      const map = this.getSelectedOptionMapFromDOM();
      if (map) this.applyAltVariantFilter(map);
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
